import {
  ESTIMATE_PC_MSG,
  REFERRAL_MSG,
  SPLIT_MODE_LABEL,
  VENDOR_IMPORT_MSG,
  VENDOR_PANEL_MSG,
  VENDOR_QUOTE_STATUS_LABEL,
  fillLabels,
  vendorSaleChip,
  isCandidate,
  marginOfMarkup,
  markupOfRates,
  markupUnitPrice,
  pct2,
  plannedLine,
  splitShares,
  type EstimateLine,
  type Labels,
  type SplitMode,
  type VendorImportPricing,
  type VendorImportRequest,
  type VendorProgress,
  type VendorQuote,
  type VendorQuoteLine,
  type VendorQuoteProgress,
} from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { api, messageOf } from "../api.js";
import { fq, yen } from "../lib/num.js";
import { NumInput } from "./NumInput.jsx";
import { Help } from "./ui.jsx";
import { t } from "../labels.js";
import { UnitInput } from "./UnitInput.jsx";

/* ============================================================================
   外注見積から見積を作る（#84、docs/34、050）。試作 v1.2 の形をそのまま React に。
   ・紙の社内用の列：行ごとの「取込」の印（印だけ。マウスを載せると「取込」）と、出どころ（付録A）＋「外す」
   ・帯：外注見積ごとに「取込済／未取込」「売価／仕入れ」「原本」「外注見積から行をまとめて追加」（#274 で「取り込んだ／残り」から）
   ・取込の1枚（紙の上に重なる）：上＝入れる先、下＝外注の行（表紙 → 内訳。Σ・§ は選べない）、右＝原本。
     ［率で｜円で］粗利率＝上乗せ率、この行に入れる／まとめて／分けて／行にして追加
   ・原本：右から出る引き出し（460px、前／次、画面いっぱい）
   金額は DB が計算する。ここで出すのは予定（contract の plannedLine・splitShares＝同じ式）だけ
   ============================================================================ */

export type TargetRow = { id: string; role: string; name: string; spec: string; note: string; quantity: number | null; unit: string; unitPrice: number | null; costAmount: number | null; sheetId: string | null };
export type OrigState = { quoteId: string; page: number };

const IMPORTABLE = new Set<VendorQuoteLine["role"]>(["detail", "adjustment", "excluded", "zero"]);
const SUM_ROLES = new Set<VendorQuoteLine["role"]>(["subtotal", "total", "tax"]);
/** 紙の帯の名前：表紙は「表紙」（処理役の cover／Cover も）、内訳は工事名（無ければ「内訳」）。紙が1枚だけなら帯は出さない（#87 の2回目） */
export function sheetLabel(sh: string | null | undefined): string {
  const t = (sh ?? "").trim();
  if (!t || /^cover$/i.test(t) || t === "表紙") return "表紙";
  return t;
}
const isNeg = (l: VendorQuoteLine) => l.amount != null && l.amount < 0;
const readable = (q: VendorQuoteProgress) => q.status === "review" || q.status === "done" || q.status === "manual";
const ymd = (s: string | null) => (s ? s.replace(/-/g, "/") : "");

/** ％の欄（小数2桁。#213 で1桁から。全角も直す。打ち終わり＝欄を出る・Enter で親に渡す） */
function PctInput({ value, onCommit, ...rest }: Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { value: number | null; onCommit: (v: number | null) => void }) {
  const fmt = (v: number | null) => (v == null ? "" : String(Math.round(v * 100) / 100));
  const [text, setText] = useState(() => fmt(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(fmt(value));
  }, [value]);
  const commit = () => {
    const t = text.replace(/[０-９．]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[^\d.]/g, "");
    const v = t === "" ? null : Number(t);
    onCommit(v == null || Number.isNaN(v) ? null : Math.round(v * 100) / 100);
  };
  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); commit(); } }}
    />
  );
}

/** 「取込」の印（紙 → 行）。44×44、印だけ。マウスを載せると「取込」 */
export function ImportIcon({ done, onClick, disabled }: { done: boolean; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={"imp" + (done ? " done" : "")} aria-label="取込" data-tip="取込" title="取込" disabled={disabled} data-testid="vimp" onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 3h7l3 3v6" />
        <path d="M11 3v3h3" />
        <path d="M6 8h5M6 11h5" />
        <path d="M4 15h16v6H4z" />
        <path d="M4 18h16" />
        <path d="M14 9l3 3-3 3" />
        <path d="M17 12h-6" />
      </svg>
    </button>
  );
}

/** 仕入れの出どころ（付録A）：「沼田電気 N-2026-02 ／ 解除」「3行から（…）」「電工費 150,000 のうち」「別途」。手で打った行には何も書かない。
 *  #312（11/5）：「外注 {正式名称} {番号}」をやめ「{略称} {番号}」（略称が空なら正式名称から「株式会社」などを外した名前＝displayNameOf）。乗せると正式名称と番号 */
export function CostSource({ costRef, role, onUnlink }: { costRef: NonNullable<EstimateLine["costRef"]>; role: string; onUnlink?: (() => void) | undefined }) {
  const srcs = costRef.sources;
  let detail: string | null = null;
  if (costRef.mode === "merge" && srcs.length) detail = `${srcs.length}行から（${srcs.map((s) => s.name).join("・")}）`;
  else if (costRef.mode === "split" && srcs[0]) detail = `${srcs[0].name} ${yen(srcs[0].base)} のうち`;
  return (
    <span className="src" data-testid="cost-src" onClick={(e) => e.stopPropagation()}>
      <Link className="costref" to={`/vendor-quotes/${costRef.vendorQuoteId}`} title={`${costRef.vendorName || costRef.vendorShort || ""} ${costRef.quoteNo}`.trim()} data-testid="cost-ref">
        {role === "excluded" ? "別途" : `${costRef.vendorShort || costRef.vendorName} ${costRef.quoteNo}`.trim()}
      </Link>
      {detail ? <span className="srcd">{detail}</span> : null}
      {onUnlink ? (
        <button type="button" className="btn text" data-testid="unlink" onClick={onUnlink}>解除</button>
      ) : null}
    </span>
  );
}

/* ---------------------------------------------------------------- 帯（紙の上） */
export function VendorBand({ progress, labels, readOnly, onBulk, onOriginal }: { progress: VendorProgress | undefined; labels: Labels; readOnly: boolean; onBulk: (quoteId: string) => void; onOriginal: (quoteId: string) => void }) {
  if (!progress?.quotes.length) return null;
  return (
    <div className="vbands" data-testid="vendor-bands">
      {progress.quotes.map((q) => {
        const name = `${q.vendorName || labels.vendor} ${q.quoteNo}`.trim();
        if (!readable(q)) {
          return (
            <div key={q.vendorQuoteId} className="vband quiet" data-testid="vendor-band">
              <b>{name}</b>
              <span className="muted">{q.status === "failed" ? VENDOR_QUOTE_STATUS_LABEL.failed : VENDOR_QUOTE_STATUS_LABEL.reading}</span>
            </div>
          );
        }
        const allDone = q.restCount === 0 && q.allCount > 0;
        // #274（docs/99 §4）：見積書の帯と同じ札（取込済／未取込／全行取込済・原価割れ／粗利）
        const sale = vendorSaleChip(q, yen);
        return (
          <div key={q.vendorQuoteId} className={"vband" + (sale?.tone === "shu" ? " shu" : "")} data-testid="vendor-band">
            <b>{name}</b>
            <span className="num">
              {ESTIMATE_PC_MSG.vendorColDone} <b>{yen(q.doneAmount)}</b>／{allDone ? <span className="chip">{VENDOR_IMPORT_MSG.allDone}</span> : <>{ESTIMATE_PC_MSG.vendorColRest} <b>{q.restCount}行 {yen(q.restAmount)}</b></>}
            </span>
            {q.doneCount > 0 ? (
              <span className="num">
                売価 <b>{yen(q.saleAmount)}</b>／仕入れ <b>{yen(q.costAmount)}</b>
              </span>
            ) : null}
            {sale ? <span className={sale.tone === "shu" ? "chip shu" : "chip vs-margin"}>{sale.text}</span> : null}
            <span className="sp" />
            <button type="button" className="small" onClick={() => onOriginal(q.vendorQuoteId)} data-testid="band-orig">原本</button>
            {!readOnly ? (
              <button type="button" className="small ai" onClick={() => onBulk(q.vendorQuoteId)} data-testid="band-bulk">{t("{vendorQuote}から一括追加")}</button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- 原本（右からの引き出し・画面幅いっぱい） */
/** 原本の中身（PDF は iframe・画像は img）。外注見積の一覧の右側パネル（#198）も使う */
export function OrigView({ q, page }: { q: VendorQuote; page: number }) {
  const url = q.original.url;
  if (q.original.contentType === "application/pdf") {
    // Caddy の CSP は object-src 'none'（048）なので <iframe>。#page= でそのページ（Chrome・Edge）。ページの移動は PDF の表示（上の帯）で。
    // 原本の応答は API が X-Frame-Options SAMEORIGIN・frame-ancestors 'self' を付ける（#87。既定の DENY のままだと枠が白いまま）
    return <iframe key={`${url}#${page}`} src={`${url}#page=${page}&navpanes=0`} title={`原本 P.${page}`} data-testid="orig-view" />;
  }
  return <img src={url} alt="原本" data-testid="orig-view" />;
}

/**
 * 原本の引き出し（#84 から）。右からすっと出て、#87 の3回目（9/27）で画面幅いっぱいに。
 * 帯の「原本」・取込の1枚の「原本」は P.1、行の「P.n」はそのページで開く。ページの送りボタンは置かない（PDF の表示に任せる）。
 * 見積の紙（Estimate）と外注見積の中身（VendorQuotes）の両方がこれを使う。
 */
export function VendorOriginal({ orig, onClose }: { orig: OrigState | null; onClose: () => void }) {
  const q = useQuery({ queryKey: ["vendor-quote", orig?.quoteId], queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${orig!.quoteId}`), enabled: !!orig });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!orig) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    // 取込の1枚より先に受ける（capture）：Esc は 原本 → 取込の1枚 の順で閉じる
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [orig, onClose]);
  // 開いたら枠にフォーカス（PDF の中にフォーカスが入ると Esc が届かない）
  useEffect(() => {
    if (orig) ref.current?.focus();
  }, [orig?.quoteId, orig?.page]);
  const name = orig && q.data ? `${q.data.vendor?.name ?? q.data.issuerName ?? ""} ${q.data.quoteNo}（${q.data.sourceLabel}${q.data.pageCount ? ` ${q.data.pageCount}ページ` : ""}）` : "";
  return (
    <div ref={ref} tabIndex={-1} className={"vdrawer" + (orig ? " open" : "")} role="dialog" aria-label="原本" aria-hidden={!orig} data-testid="orig-drawer">
      <div className="dh">
        <h2>原本</h2>
        <span className="hint" data-testid="orig-name">{name}</span>
        <span className="sp" />
        {orig && q.data ? <a className="btn text" href={q.data.original.url} target="_blank" rel="noreferrer">別ウィンドウで表示</a> : null}
        <button type="button" className="close" aria-label="閉じる" onClick={onClose} data-testid="orig-close">×</button>
      </div>
      <div className="db">{orig && q.data ? <OrigView q={q.data} page={orig.page} /> : orig && q.isError ? <span className="shu">{messageOf(q.error)}</span> : null}</div>
    </div>
  );
}

/* ---------------------------------------------------------------- 取込の1枚 */
export type PickerProps = {
  progress: VendorProgress;
  quoteId: string;
  onQuoteChange: (id: string) => void;
  /** 入れる先の行（0＝まとめて追加、1＝この行に、2以上＝分ける） */
  targets: TargetRow[];
  rowNo: (id: string) => number;
  amountOf: (r: TargetRow) => number;
  issued: boolean;
  pricing: VendorImportPricing;
  onPricing: (p: VendorImportPricing) => void;
  onEditTarget: (id: string, patch: Partial<TargetRow>) => void;
  onAddTarget: () => void;
  onRemoveTarget: (id: string) => void;
  onImport: (req: Omit<VendorImportRequest, "sheetId" | "afterLineId"> & { afterLineId: string | null }) => Promise<boolean>;
  onClose: () => void;
  /** 原本をそのページで画面いっぱいに開く（行の「P.n」・上の「原本」） */
  onOriginal: (page: number) => void;
  /** #282：取込済の外注の行の「解除」＝その見積の行の{vendorQuote}連携解除（POST …/vendor-unlink） */
  onUnlink?: (lineId: string) => void;
  /** 原本（画面いっぱい）が開いているあいだは Esc で閉じない */
  origOpen: boolean;
  labels: Labels;
  /**
   * #213（docs/40 §5.4）：見積のパートナーの率（パートナー ON の見積だけ。無ければ null）。
   * 「粗利率［ ］％ ＋ パートナー［ ］％ ＝ 上乗せ率［ ］％」で、取り込んだ時点で単価にパートナーの分が入る（★C）
   */
  referralPct?: number | null | undefined;
};

export function VendorImportPicker(p: PickerProps) {
  const { progress, quoteId, targets, issued, pricing, labels } = p;
  const prog = progress.quotes.find((q) => q.vendorQuoteId === quoteId);
  const q = useQuery({ queryKey: ["vendor-quote", quoteId], queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${quoteId}`) });
  const [sel, setSel] = useState<Set<string>>(() => {
    // 1行に入れるとき：その行に入っている外注の行を選んだ状態で開く
    const t = targets.length === 1 ? targets[0] : undefined;
    return new Set(t ? (prog?.imported ?? []).filter((m) => m.lineId === t.id).map((m) => m.vendorLineId) : []);
  });
  const [splitMode, setSplitMode] = useState<SplitMode>("sale");
  const [manual, setManual] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !p.origOpen) p.onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [p]);

  const lines = q.data?.lines ?? [];
  const taxCtx = { taxIncluded: q.data?.taxIncluded ?? false, taxRate: q.data?.taxRate ?? 10 };
  const exOf = (l: VendorQuoteLine) => plannedLine(l, taxCtx, pricing, progress.round).cost;
  /** 取込済み：外注の行 → 見積の行（どの行に入ったか） */
  const used = useMemo(() => {
    const m = new Map<string, { lineNo: number; name: string; lineId: string; sheetName: string | null }[]>();
    for (const x of prog?.imported ?? []) m.set(x.vendorLineId, [...(m.get(x.vendorLineId) ?? []), { lineNo: p.rowNo(x.lineId) || x.lineNo, name: x.name, lineId: x.lineId, sheetName: x.sheetName ?? null }]);
    return m;
  }, [prog]);
  const single = targets.length === 1;
  const multi = targets.length >= 2;
  const target0 = targets[0];
  /** 候補（決めごと13）：1行に入れるとき、名前が似た外注の行 */
  const cand = useMemo(() => new Set(single && target0?.name ? lines.filter((l) => IMPORTABLE.has(l.role) && !used.has(l.id) && isCandidate(target0.name, l)).map((l) => l.id) : []), [lines, single, target0?.name, used]);
  const selLines = lines.filter((l) => sel.has(l.id));
  const nSel = selLines.length;
  /** 選んだ順（sel は Set＝入れた順。#141「この行と下に入れる」は1つ目をこの行に、残りをこの行の下にこの順で） */
  const selOrdered = [...sel].map((id) => lines.find((l) => l.id === id)).filter((l): l is VendorQuoteLine => !!l);
  const posN = selLines.filter((l) => !isNeg(l)).length;
  const selAmt = selLines.reduce((t, l) => t + exOf(l), 0);
  const onlyNeg = nSel > 0 && selLines.every(isNeg);
  const hasNeg = selLines.some(isNeg);
  /** 行から開いて2行以上選んだ：主ボタンは「この行と下に入れる」（#141） */
  const below = single && nSel >= 2;
  const marginPct = pricing.kind === "margin" ? pricing.value : null;
  /** #213：パートナー ON の見積だけ（粗利率と合わせて 100% 未満） */
  const refPct = pricing.kind === "margin" && p.referralPct != null ? (pricing.referralPct ?? p.referralPct) : null;
  const markupPct = marginPct == null ? null : pct2(markupOfRates(marginPct, refPct ?? 0));
  const priceOf = (cost: number) => (pricing.kind === "yen" ? cost + pricing.value * (multi || (single && !below) ? 1 : below ? posN : nSel) : (markupUnitPrice(cost, pricing.value + (refPct ?? 0), progress.round) ?? 0));
  const rest = lines.filter((l) => IMPORTABLE.has(l.role) && !used.has(l.id));
  const restAmt = rest.reduce((t, l) => t + exOf(l), 0);
  const importable = lines.filter((l) => IMPORTABLE.has(l.role));
  const doneN = importable.length - rest.length;

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const readyQuotes = progress.quotes.filter(readable);

  /* 分ける：仕入れの分け方（付録B） */
  const splitTotal = multi && nSel === 1 ? exOf(selLines[0]!) : 0;
  const shares = multi && nSel === 1 ? splitShares(splitTotal, targets.map((t) => ({ amount: p.amountOf(t), quantity: t.quantity, manual: manual[t.id] ?? 0 })), splitMode) : [];
  const splitRest = splitTotal - shares.reduce((a, b) => a + b, 0);

  const run = async (mode: VendorImportRequest["mode"]) => {
    if (busy) return;
    setBusy(true);
    try {
      // intoBelow は選んだ順のまま（値引きも送る＝API が飛ばして数える）
      const ids = mode === "append" ? selLines.filter((l) => !isNeg(l)).map((l) => l.id) : mode === "intoBelow" ? selOrdered.map((l) => l.id) : selLines.map((l) => l.id);
      const base = { vendorQuoteId: quoteId, mode, lineIds: ids, pricing, targetLineId: null as string | null, afterLineId: null as string | null, shares: [] as { lineId: string; amount: number }[], splitMode };
      let ok = false;
      if (mode === "into" || mode === "merge" || mode === "intoBelow") ok = await p.onImport({ ...base, targetLineId: target0!.id });
      else if (mode === "split") ok = await p.onImport({ ...base, shares: targets.map((t, k) => ({ lineId: t.id, amount: shares[k] ?? 0 })) });
      else ok = await p.onImport({ ...base, afterLineId: targets.length ? targets[targets.length - 1]!.id : null });
      if (ok) p.onClose();
    } finally {
      setBusy(false);
    }
  };

  // 下の注意は「いま押すと困ること」だけ（値引きの行・#141 名称の置き換え）。ボタンの意味は「入れる先」の「？」へ（#92）
  let note = "";
  let noteShu = false;
  if (onlyNeg) { note = VENDOR_IMPORT_MSG.negative; noteShu = true; }
  else {
    const notes: string[] = [];
    // 入れる先の行に名称があり、写すと変わる（この行に入れる・この行と下に入れる。出した回は名称を触らない）
    const first = selOrdered.find((l) => !isNeg(l));
    if (single && !issued && first && target0?.name.trim() && first.name.trim() !== target0.name.trim()) notes.push(t(VENDOR_IMPORT_MSG.replaceName));
    if (hasNeg && !single && !multi) notes.push(VENDOR_IMPORT_MSG.negativeAppend);
    else if (hasNeg && below && !issued) notes.push(VENDOR_IMPORT_MSG.negativeIntoBelow);
    note = notes.join("。");
  }

  let curSheet: string | null | undefined;
  const rows: React.ReactNode[] = [];
  const manySheets = new Set(lines.map((l) => l.sheetName ?? "")).size > 1;
  for (const l of lines) {
    const sh = l.sheetName ?? "";
    if (manySheets && sh !== curSheet) {
      curSheet = sh;
      rows.push(
        <tr key={`sh-${l.id}`} className="sheet">
          <td colSpan={9}>{sheetLabel(sh)}</td>
        </tr>,
      );
    }
    const imp = IMPORTABLE.has(l.role);
    const u = used.get(l.id);
    const isCand = cand.has(l.id) && !u;
    const ns = !imp ? (SUM_ROLES.has(l.role) ? "ns sum" : "ns hd") : "";
    let st: React.ReactNode = null;
    if (!imp) st = <span className="muted">{l.role === "subtotal" ? "小計" : l.role === "total" ? "合計" : l.role === "tax" ? "消費税" : l.role === "discount" ? "値引き" : l.role === "rate" ? "率" : "見出し"}</span>;
    else if (u)
      // #282（docs/99 §13）：どの見積の行に入っているかを文字で（「取込済 → 行 3 銅材（B案）」）。右の「解除」はその行の連携解除
      st = (
        <span className="vl-used" data-testid="vl-used">
          {u.map((x) => (
            <span key={x.lineId} className="vl-usedrow">
              <b className="shu">{VENDOR_PANEL_MSG.usedTo.replace("{no}", String(x.lineNo)).replace("{name}", x.name || t("（{lineName}なし）"))}{x.sheetName ? `（${x.sheetName}）` : ""}</b>
              {p.onUnlink ? (
                <button type="button" className="btn text sm" onClick={() => p.onUnlink!(x.lineId)} aria-label={`行 ${x.lineNo} の${t(ESTIMATE_PC_MSG.menuUnlink)}`} data-testid="vl-unlink">
                  {VENDOR_PANEL_MSG.unlink}
                </button>
              ) : null}
            </span>
          ))}
        </span>
      );
    else if (l.role === "excluded") st = <span className="chip quiet">別途</span>;
    else if (isNeg(l)) st = <span className="chip quiet">{VENDOR_PANEL_MSG.negOnlyMerge}</span>;
    else if (isCand) st = <span className="chip ai" data-testid="vl-cand">候補</span>;
    const checked = sel.has(l.id);
    rows.push(
      <tr
        key={l.id}
        data-testid="vl"
        data-vl={l.id}
        className={`${checked ? "sel" : ""} ${u ? "used" : ""} ${isCand ? "cand" : ""} ${ns}`}
        onClick={(e) => { if (!imp) return; if ((e.target as HTMLElement).closest("input,button,a")) return; toggle(l.id); }}
      >
        <td className="ck">{imp ? <input type="checkbox" className="pick" checked={checked} onChange={() => toggle(l.id)} aria-label={`${l.name} を選択`} /> : null}</td>
        <td className="n">{l.name}</td>
        <td className="s">{l.spec}</td>
        <td className="r num">{SUM_ROLES.has(l.role) ? "" : `${l.quantity == null ? "" : fq(l.quantity)} ${l.unit}`}</td>
        <td className="r num">{l.unitPrice == null ? "" : yen(l.unitPrice)}</td>
        <td className="r num">{l.amount == null ? l.note : yen(l.amount)}</td>
        <td className="m">{l.remarks}</td>
        <td className="st">{st}</td>
        <td className="st">{l.pageNo ? <button type="button" className="btn text" onClick={() => p.onOriginal(l.pageNo!)} aria-label={`原本 P.${l.pageNo} 表示`}>P.{l.pageNo}</button> : null}</td>
      </tr>,
    );
  }
  const candN = [...cand].length;
  const howHelp = [
    multi
      ? t("「分割して取込」は{vendorQuote}の1行を、上の行に仕入れを分けて入れます。")
      : single
        ? issued
          ? null
          : t("「この行に取込」は {lineName}・{lineSpec}・数量・単位・単価・金額・{lineNote} と仕入れを写します（{lineNote}は紙に出ます）。2行以上選ぶと「この行と下に取込」：選んだ順に1行目をこの行に、残りをこの行の下に1行ずつ追加します。「合算してこの行に取込」は{lineName}を空にして仕入れを合計します。「新規行に追加」はこの行の下に追加します。")
        : "新しい行として、この見積の今の紙のいちばん下に追加します（1行ずつ、1対1）。値引きの行は、どれかの行に合算して取り込むときだけ合計に入ります。",
    candN ? "「候補」は名前が似た行です。押すと選べます。" : null,
  ]
    .filter(Boolean)
    .join("");

  return (
    <div className="vlayer" data-testid="vimp-layer" onMouseDown={(e) => { if (e.target === e.currentTarget) p.onClose(); }}>
      <div className="vpick" role="dialog" aria-label={t(VENDOR_PANEL_MSG.title)}>
        <div className="ph">
          <h2>{t(VENDOR_PANEL_MSG.title)}</h2>
          {readyQuotes.length > 1 ? (
            <select aria-label={t("どの{vendorQuote}か")} value={quoteId} onChange={(e) => { p.onQuoteChange(e.target.value); setSel(new Set()); }}>
              {readyQuotes.map((x) => (
                <option key={x.vendorQuoteId} value={x.vendorQuoteId}>{`${x.vendorName || labels.vendor} ${x.quoteNo}`.trim()}</option>
              ))}
            </select>
          ) : null}
          <span className="hint" data-testid="vimp-head">
            {prog ? `${prog.vendorName || labels.vendor} ${prog.quoteNo}（${ymd(prog.quotedOn)}${prog.totalExTax != null ? `、税抜 ${yen(prog.totalExTax)}` : ""}）` : ""}
            {q.data?.taxIncluded ? <span className="chip quiet">{VENDOR_PANEL_MSG.taxIncluded.replace("{rate}", String(q.data.taxRate))}</span> : null}
          </span>
          {issued ? <span className="chip quiet">{VENDOR_PANEL_MSG.issued}</span> : null}
          <span className="sp" />
          {candN ? (
            <>
              <span className="chip ai">候補 {candN}</span>
              <span className="hint">{VENDOR_PANEL_MSG.cand}</span>
            </>
          ) : null}
          <button type="button" className="small" onClick={() => p.onOriginal(1)} data-testid="vimp-orig">原本</button>
          <button type="button" className="close" aria-label="閉じる" onClick={p.onClose} data-testid="vimp-close">×</button>
        </div>

        <div className="vtarget">
          <div className="t">
            {VENDOR_PANEL_MSG.target}：{targets.length ? VENDOR_PANEL_MSG.targetLines.replace("{nos}", targets.map((t) => p.rowNo(t.id)).join("・")) : VENDOR_PANEL_MSG.targetNew}
            {howHelp ? <Help text={howHelp} label={VENDOR_PANEL_MSG.howTitle} testId="help-vimp" /> : null}
            <span className="sp" />
            {(single || multi) && !issued ? <button type="button" className="small" onClick={p.onAddTarget} data-testid="add-target">{VENDOR_PANEL_MSG.addSplit}</button> : null}
          </div>
          {targets.length ? (
            <table className="vtg">
              <thead>
                <tr><th style={{ width: 36 }} /><th>{labels.lineName}</th><th style={{ width: "16%" }}>{labels.lineSpec}</th><th className="r" style={{ width: 150 }}>数量 単位</th><th className="r" style={{ width: 110 }}>単価</th><th className="r" style={{ width: 110 }}>金額</th><th style={{ width: "14%" }}>{labels.lineNote}</th><th className="r" style={{ width: 130 }}>仕入れ</th></tr>
              </thead>
              <tbody>
                {targets.map((t, k) => (
                  <tr key={t.id} data-testid="target">
                    <td className="num muted">{p.rowNo(t.id)}</td>
                    <td>{issued ? t.name || `（${labels.lineName}なし）` : <input className={"cell" + (!t.name && (multi || used.size) ? " needname" : "")} value={t.name} placeholder={`${labels.lineName}（${labels.customer}に出る名前）`} aria-label={labels.lineName} onChange={(e) => p.onEditTarget(t.id, { name: e.target.value })} />}</td>
                    <td>{issued ? t.spec : <input className="cell" value={t.spec} placeholder={labels.lineSpec} aria-label={labels.lineSpec} onChange={(e) => p.onEditTarget(t.id, { spec: e.target.value })} />}</td>
                    <td className="r">
                      {issued ? `${t.quantity == null ? "" : fq(t.quantity)} ${t.unit}` : (
                        <span className="cellwrap">
                          <NumInput kind="dec" className="cell r" style={{ width: "5em" }} value={t.quantity} aria-label="数量" onChange={(v) => p.onEditTarget(t.id, { quantity: v })} />
                          <UnitInput className="cell" style={{ width: "3.5em" }} value={t.unit} onChange={(v) => p.onEditTarget(t.id, { unit: v })} />
                        </span>
                      )}
                    </td>
                    <td className="r">{issued ? (t.unitPrice == null ? "" : yen(t.unitPrice)) : <NumInput kind="int" className="cell r" value={t.unitPrice} aria-label="単価" onChange={(v) => p.onEditTarget(t.id, { unitPrice: v, ...(v != null && t.quantity == null ? { quantity: 1, unit: t.unit || "式" } : {}) })} />}</td>
                    <td className="r num">{yen(p.amountOf(t))}</td>
                    <td>{issued ? t.note : <input className="cell" value={t.note} placeholder={labels.lineNote} aria-label={labels.lineNote} onChange={(e) => p.onEditTarget(t.id, { note: e.target.value })} />}</td>
                    <td className="r num">
                      {t.costAmount == null ? "—" : yen(t.costAmount)}
                      {k > 0 && !issued ? <> <button type="button" className="btn text" onClick={() => p.onRemoveTarget(t.id)}>削除</button></> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {multi && nSel === 1 ? (
            <div className="vsplit" data-testid="split-box">
              <div className="hrow" style={{ margin: "0 0 4px" }}>
                <b>{VENDOR_PANEL_MSG.splitHead.replace("{name}", selLines[0]!.name).replace("{amount}", yen(splitTotal)).replace("{n}", String(targets.length))}</b>
                <span className="seg sm" role="group" aria-label="仕入れの分け方">
                  {(["sale", "qty", "manual"] as SplitMode[]).map((m) => (
                    <button key={m} type="button" aria-pressed={splitMode === m} onClick={() => setSplitMode(m)}>{SPLIT_MODE_LABEL[m]}</button>
                  ))}
                </span>
                <span className="sp" />
                <span className={"num" + (splitRest ? " shu" : "")} data-testid="split-rest">残り <b>{yen(splitRest)}</b> 円</span>
              </div>
              <table>
                <tbody>
                  <tr>
                    {targets.map((t, k) => (
                      <Fragment key={t.id}>
                        <td>{p.rowNo(t.id)} {t.name || `（${labels.lineName}なし）`}</td>
                        <td className="r">{splitMode === "manual" ? <NumInput kind="int" value={manual[t.id] ?? shares[k] ?? 0} aria-label="仕入れの分け" onChange={(v) => setManual((m) => ({ ...m, [t.id]: v ?? 0 }))} /> : <span className="num">{yen(shares[k] ?? 0)}</span>}</td>
                      </Fragment>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div className="vpbody">
          <div>
            <div className="vprog" data-testid="vimp-prog">
              {/* #283（docs/99 §14）：「全 14行｜取込済 2行｜未取込 12行 84,539円」（#274 の帯と同じ言葉） */}
              <span>{VENDOR_PANEL_MSG.all.replace("{n}", String(importable.length))}</span>
              <span className="vbar" aria-hidden="true">｜</span>
              <span className="done">{VENDOR_PANEL_MSG.done.replace("{n}", String(doneN))}</span>
              <span className="vbar" aria-hidden="true">｜</span>
              <span>{VENDOR_PANEL_MSG.rest.replace("{n}", String(rest.length)).replace("{amount}", yen(restAmt))}</span>
              {importable.length && rest.length === 0 ? <span className="chip">{VENDOR_IMPORT_MSG.allDone}</span> : <button type="button" className="small" onClick={() => setSel(new Set(rest.map((l) => l.id)))} data-testid="pick-rest" disabled={!rest.length}>{ESTIMATE_PC_MSG.vendorPickRest}</button>}
              <button type="button" className="small" onClick={() => setSel(new Set())} disabled={!nSel}>選択解除</button>
              <span className="sp" />
              <span className="num" data-testid="n-sel-text">{VENDOR_PANEL_MSG.selected.replace("{n}", String(nSel)).replace("{amount}", yen(selAmt))}</span>
              <span hidden data-testid="n-sel">{nSel}</span>
            </div>
            {q.isPending ? <p className="hint">{VENDOR_PANEL_MSG.loading}</p> : q.isError ? <p className="shu">{messageOf(q.error)}</p> : (
              <table className="vl" data-testid="vl-table">
                {/* 列の幅：名称を広く、規格は名称の 2/3、数字は固定（#87 の2回目） */}
                <colgroup>
                  <col style={{ width: 36 }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: 90 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: "13%" }} />
                  <col />
                  <col style={{ width: 56 }} />
                </colgroup>
                <thead><tr><th /><th>{labels.lineName}</th><th>{labels.lineSpec}</th><th className="r">数量</th><th className="r">単価</th><th className="r">金額</th><th>{labels.lineNote}</th><th>状態</th><th>原本</th></tr></thead>
                <tbody>{rows}</tbody>
              </table>
            )}
          </div>
        </div>

        <div className="vpfoot" data-notice-above="">
          <div className="vmk">
            {/* #314：見積書の行の詳細と同じ「単価［粗利率｜上乗せ］」。切替が欄の名前（欄の頭に重ねて出さない） */}
            <span className="muted">{REFERRAL_MSG.pricingHead}</span>
            <span className="seg sm" role="group" aria-label={REFERRAL_MSG.pricingBy}>
              <button type="button" aria-pressed={pricing.kind === "margin"} onClick={() => p.onPricing({ kind: "margin", value: marginPct ?? progress.marginPct, ...(p.referralPct != null ? { referralPct: p.referralPct } : {}) })}>{VENDOR_PANEL_MSG.byRate}</button>
              <button type="button" aria-pressed={pricing.kind === "yen"} onClick={() => p.onPricing({ kind: "yen", value: pricing.kind === "yen" ? pricing.value : 0 })}>{VENDOR_PANEL_MSG.byYen}</button>
            </span>
            {pricing.kind === "margin" ? (
              <>
                <label><PctInput value={pricing.value} aria-label={REFERRAL_MSG.margin} data-testid="margin-pct" onCommit={(v) => { const m = v ?? 0; if (m >= 0 && m + (refPct ?? 0) < 100) p.onPricing({ kind: "margin", value: m, ...(refPct != null ? { referralPct: refPct } : {}) }); }} /> ％</label>
                {refPct != null ? (
                  <>
                    <span className="eq">＋</span>
                    <label>{t(REFERRAL_MSG.referral)} <PctInput value={refPct} aria-label={t(REFERRAL_MSG.referral)} data-testid="referral-pct-import" onCommit={(v) => { const f = v ?? 0; if (f >= 0 && f < 90 && pricing.value + f < 100) p.onPricing({ kind: "margin", value: pricing.value, referralPct: f }); }} /> ％</label>
                  </>
                ) : null}
                <span className="eq">＝</span>
                <label>{REFERRAL_MSG.markup} <PctInput value={markupPct} aria-label={REFERRAL_MSG.markup} data-testid="markup-pct" onCommit={(v) => { if (v == null || v < 0) return; const m = Math.round((marginOfMarkup(v) - (refPct ?? 0)) * 100) / 100; if (m >= 0) p.onPricing({ kind: "margin", value: m, ...(refPct != null ? { referralPct: refPct } : {}) }); }} /> ％</label>
                <Help text={fillLabels(refPct != null ? VENDOR_IMPORT_MSG.helpMarginRef : VENDOR_IMPORT_MSG.helpMargin, labels).replace("{round}", String(progress.round))} label={REFERRAL_MSG.helpLabel} testId="help-mk" />
              </>
            ) : (
              <>
                <label><NumInput kind="int" value={pricing.value} aria-label={`${REFERRAL_MSG.markupYen}（円）`} data-testid="markup-yen" onChange={(v) => p.onPricing({ kind: "yen", value: v ?? 0 })} /> 円{single || multi ? "" : "（行ごと）"}</label>
                <Help text={VENDOR_IMPORT_MSG.helpYen.replace("{round}", String(progress.round))} label={`${REFERRAL_MSG.markupYen}の説明`} testId="help-yen" />
              </>
            )}
            <span className="prev" data-testid="vimp-preview">
              {nSel ? (
                <>
                  {VENDOR_PANEL_MSG.selected.replace("{n}", String(nSel)).replace("{amount}", yen(selAmt))} <b>{VENDOR_PANEL_MSG.toSale.replace("{amount}", yen(priceOf(selAmt)))}</b>
                </>
              ) : null}
            </span>
          </div>
          <div className="vacts">
            {single ? (
              <>
                {below ? (
                  // #141：2行以上選んだら主は「この行と下に入れる」。行を足すので出した回は押せない（行にして追加と同じ）
                  <button type="button" className="btn primary" disabled={busy || posN < 1 || issued} onClick={() => void run("intoBelow")} data-testid="btn-into-below">この行と下に取込（{posN}行）</button>
                ) : (
                  <button type="button" className="btn primary" disabled={busy || nSel !== 1 || onlyNeg} onClick={() => void run("into")} data-testid="btn-into">{VENDOR_PANEL_MSG.into}</button>
                )}
                <button type="button" className="btn" disabled={busy || nSel < 2 || onlyNeg} onClick={() => void run("merge")} data-testid="btn-merge">{VENDOR_PANEL_MSG.merge}</button>
              </>
            ) : multi ? (
              <button type="button" className="btn primary" disabled={busy || nSel !== 1 || onlyNeg} onClick={() => void run("split")} data-testid="btn-split">{VENDOR_PANEL_MSG.split}</button>
            ) : null}
            <button type="button" className={"btn" + (single || multi ? "" : " primary")} disabled={busy || nSel < 1 || onlyNeg || issued} onClick={() => void run("append")} data-testid="btn-append">
              {VENDOR_PANEL_MSG.append}{nSel ? `（${selLines.filter((l) => !isNeg(l)).length}行）` : ""}
            </button>
            <button type="button" className="btn quiet" onClick={p.onClose}>取消</button>
            <span className={"note" + (noteShu ? " shu" : "")} data-testid="vimp-note">{note}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
